import React from 'react';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { changeStyleInTag } from 'core/utils/domUtils';
import { DesignSystemContext, DesignSystemContextProvider, Direction } from '@drone-js/core';
import { useSelector } from 'react-redux';
import {
    selectBehaviorFromBootstrap,
    selectExperimentsFromBootstrap,
    selectLocaleFromBootstrap,
    selectMobileDeviceTypeFromBootstrap,
    selectRtlFromBootstrap,
} from 'store/slice/bootstrap';
import { ThemeProp } from '@kite-js/core/lib/types/ThemeProps';
import { mapDesignSystemExperiments } from 'app/DesignSystemExperiments';
import { Locale } from '@drone-js/core/src/contexts/session/Locale';
import { whitelabelThemeProvider } from '@drone-js/whitelabels';
import { whitelabelThemeProvider as kiteWhitelabelThemeProvider } from '@kite-js/whitelabel';
import { MobileDeviceType } from 'core/type/BootstrapConfig.types';
import { PlatformContext } from '@drone-js/core/src/contexts/session/Platform';

// TODO: Remove this style override after CartJS migrated to Drone
const OverrideGlobalStyle = createGlobalStyle`
    [data-element-name="add-to-cart"],
    [data-element-name="proceed-to-cart"],
    [data-element-name="book-this-room-only"] {
        border-radius: 100vh !important;
    }
`;

// TODO: Remove this after CITI after CITI migrated to Drone
const OverrideCitiGlobalStyle = createGlobalStyle`
    body {
        button {
            border-radius: ${(props: Required<ThemeProp>) => props.theme.radius.S} !important;
        }

        [role="tablist"] ~ div button,
        [data-testid="activities-selected-filter"] ~ div button,
        [data-element-name="activities-sort-and-filter-open-button"],
        [data-testid="activities-quick-category-filter-chip-item"] {
            border-radius: 100vh !important;
        }
        [data-element-name="activities-available-option-card-time"] {
            border-radius: 100vh !important;
            box-shadow: rgba(0, 0, 0, 0.2) 0 1px 3px 1px;
            transition: all 0.15s ease-in-out 0s;
            border: none;
        }
    }
`;

const deviceMapper: Partial<Record<MobileDeviceType, PlatformContext['platform']>> = {
    [MobileDeviceType.iOS]: 'ios',
    [MobileDeviceType.Android]: 'android',
};

export const WhiteLabelThemeProvider: React.FC<React.PropsWithChildren<{ whiteLabelId: number }>> = (props) => {
    const theme = React.useMemo(() => kiteWhitelabelThemeProvider(props.whiteLabelId), [props.whiteLabelId]);

    const experiments = useSelector(selectExperimentsFromBootstrap);
    const deviceType = useSelector(selectMobileDeviceTypeFromBootstrap);
    const locale = useSelector(selectLocaleFromBootstrap) as Locale | undefined;
    const isRtl = useSelector(selectRtlFromBootstrap);
    const activitiesCustomGlobalThemeStyle = useSelector(selectBehaviorFromBootstrap).common.activitiesCustomGlobalThemeStyle;

    const droneCtx = React.useMemo<DesignSystemContext>(
        () => ({
            theme: whitelabelThemeProvider(props.whiteLabelId),
            direction: isRtl ? Direction.RTL : Direction.LTR,
            experiment: mapDesignSystemExperiments(experiments),
            locale,
            platform: deviceMapper[deviceType] || 'web',
        }),
        [props.whiteLabelId, experiments, locale, isRtl, deviceType],
    );

    React.useEffect(() => {
        changeStyleInTag('html', { backgroundColor: theme.palette.background[20] });
    }, [theme]);

    return (
        <ThemeProvider theme={theme}>
            {activitiesCustomGlobalThemeStyle ? <OverrideCitiGlobalStyle theme={theme} /> : <OverrideGlobalStyle theme={theme} />}
            <DesignSystemContextProvider value={droneCtx}>{props.children}</DesignSystemContextProvider>
        </ThemeProvider>
    );
};
WhiteLabelThemeProvider.displayName = 'WhiteLabelThemeProvider';
